import { twApi } from './../api';
import { PaginateResponse } from './../../Global.model';
import {
  FuelSurchargeTableAttributes,
  SearchFuelSurchargeTableRequest,
  SearchFuelSurchargeTableResponseDto,
  GetAllFuelSurchargeTablesResponseDto,
  CreateFuelSurchargeTableRequest,
  UpdateFuelSurchargeTableRequest,
  CreateFuelSurchargeTableResponseDto,
  UpdateFuelSurchargeTableResponseDto,
  GetDieselPriceDataRequest,
  GetEiaDieselPriceDataResponseDto,
  GetFuelSurchargeTableResponseDto,
  UploadFuelSurchargeTableDocumentResponseDto,
  UploadFuelSurchargeTableDocumentRequest
} from './fuelSurchargeTable.model';

const fuelSurchargeTableApi = twApi.injectEndpoints({
  endpoints: (builder) => ({
    getFuelSurchargeTableById: builder.query<GetFuelSurchargeTableResponseDto, string>({
      query: (id) => `/fuel-surcharge-tables/${id}`,
      transformResponse: (response: { data: GetFuelSurchargeTableResponseDto }) => response.data,
      providesTags: (result) =>
        result
          ? [{ type: 'FuelSurchargeTables', id: result.id }]
          : [{ type: 'FuelSurchargeTables', id: 'FUEL_SURCHARGE_TABLES_LIST' }]
    }),
    getAllFuelSurchargeTables: builder.query<
      GetAllFuelSurchargeTablesResponseDto[],
      { divisionId?: string }
    >({
      query: ({ divisionId }) => ({
        url: '/fuel-surcharge-tables',
        params: {
          division_id: divisionId
        }
      }),
      transformResponse: (response: { data: FuelSurchargeTableAttributes[] }) => response.data,
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: 'FuelSurchargeTables' as const, id })),
              { type: 'FuelSurchargeTables', id: 'FUEL_SURCHARGE_TABLES_LIST' }
            ]
          : [{ type: 'FuelSurchargeTables', id: 'FUEL_SURCHARGE_TABLES_LIST' }]
    }),
    searchFuelSurchargeTable: builder.query<
      PaginateResponse<SearchFuelSurchargeTableResponseDto>,
      SearchFuelSurchargeTableRequest
    >({
      query: (arg) => ({
        url: '/fuel-surcharge-tables/search',
        method: 'POST',
        params: { ...arg.params },
        body: { ...arg.filters }
      }),
      transformResponse: (response: {
        data: SearchFuelSurchargeTableResponseDto[];
        more_records: boolean;
        size: number;
        total: number;
      }) => ({
        content: response.data,
        more_records: response.more_records,
        size: response.size,
        total: response.total
      }),
      providesTags: (result) =>
        result?.content
          ? [
              ...result.content.map(({ id }) => ({ type: 'FuelSurchargeTables' as const, id })),
              { type: 'FuelSurchargeTables', id: 'FUEL_SURCHARGE_TABLES_LIST' }
            ]
          : [{ type: 'FuelSurchargeTables', id: 'FUEL_SURCHARGE_TABLES_LIST' }]
    }),
    createFuelSurchargeTable: builder.mutation<
      CreateFuelSurchargeTableResponseDto,
      CreateFuelSurchargeTableRequest
    >({
      query: (body) => ({
        url: '/fuel-surcharge-tables',
        method: 'POST',
        body
      }),
      transformResponse: (response: { data: CreateFuelSurchargeTableResponseDto }) => ({
        ...response.data
      }),
      invalidatesTags: [
        { type: 'FuelSurchargeTables', id: 'FUEL_SURCHARGE_TABLES_LIST' },
        { type: 'RateTables', id: 'RATE_TABLES_LIST' }
      ]
    }),
    updateFuelSurchargeTable: builder.mutation<
      UpdateFuelSurchargeTableResponseDto,
      UpdateFuelSurchargeTableRequest
    >({
      query: (body) => ({
        url: `/fuel-surcharge-tables/${body.id}`,
        method: 'PUT',
        body
      }),
      transformResponse: (response: UpdateFuelSurchargeTableResponseDto) => ({
        ...response
      }),
      invalidatesTags: (result, error, arg) => [
        { type: 'FuelSurchargeTables', id: arg.id },
        { type: 'FuelSurchargeTables', id: 'FUEL_SURCHARGE_TABLES_LIST' },
        { type: 'RateTables' }
      ]
    }),
    getDieselPriceData: builder.query<
      GetEiaDieselPriceDataResponseDto[],
      GetDieselPriceDataRequest
    >({
      query: (arg) => ({
        url: '/fuel-surcharge-tables/eia/diesel-price',
        method: 'GET',
        params: { ...arg.filters }
      }),
      transformResponse: (response: { data: GetEiaDieselPriceDataResponseDto[] }) => {
        return response.data;
      },
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ serie_id }) => ({ type: 'EiaDieselPriceData' as const, serie_id })),
              { type: 'EiaDieselPriceData', id: 'EIA_DIESEL_PRICE_DATA_LIST' }
            ]
          : [{ type: 'EiaDieselPriceData', id: 'EIA_DIESEL_PRICE_DATA_LIST' }]
    }),
    uploadFuelSurchargeTableDocument: builder.mutation<
      UploadFuelSurchargeTableDocumentResponseDto,
      UploadFuelSurchargeTableDocumentRequest
    >({
      query: (body) => {
        const formData = new FormData();

        formData.append('document', body.file);
        return {
          url: `/fuel-surcharge-tables/${body.fuelSurchargeTableId}/document`,
          method: 'POST',
          body: formData
        };
      },
      transformResponse: (response: UploadFuelSurchargeTableDocumentResponseDto) => ({
        ...response
      }),
      invalidatesTags: (result, error, arg) => [
        { type: 'FuelSurchargeTables', id: arg.fuelSurchargeTableId },
        { type: 'FuelSurchargeTables', id: 'FUEL_SURCHARGE_TABLES_LIST' }
      ]
    }),
    deleteFuelSurchargeTable: builder.mutation<void, string>({
      query: (id) => ({
        url: `/fuel-surcharge-tables/${id}`,
        method: 'DELETE'
      }),
      invalidatesTags: (result, error, arg) => [
        { type: 'FuelSurchargeTables', id: arg },
        { type: 'FuelSurchargeTables', id: 'FUEL_SURCHARGE_TABLES_LIST' }
      ]
    })
  })
});

export const {
  useGetFuelSurchargeTableByIdQuery,
  useGetAllFuelSurchargeTablesQuery,
  useSearchFuelSurchargeTableQuery,
  useCreateFuelSurchargeTableMutation,
  useUpdateFuelSurchargeTableMutation,
  useGetDieselPriceDataQuery,
  useLazyGetDieselPriceDataQuery,
  useUploadFuelSurchargeTableDocumentMutation,
  useDeleteFuelSurchargeTableMutation
} = fuelSurchargeTableApi;
